import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'

export type EngineerJob = {
  id: string
  external_ref: string
  status: 'scheduled' | 'enroute' | 'onsite' | 'complete' | 'failed' | 'cancelled'
  priority: string
  scheduled_start: string
  scheduled_end: string
  sla_resolve_by: string
  job_type_code: string
  job_type_name: string
  postcode?: string
  address_line_1?: string
}

export type EngineerTodayPayload = {
  engineer: { id: string; display_name: string; external_ref: string }
  jobs: EngineerJob[]
}

function useClient() {
  const { accessToken } = useAuth()
  return makeClient(() => accessToken)
}

export function useEngineerToday() {
  const client = useClient()
  return useQuery({
    queryKey: ['engineer', 'today'],
    queryFn: () => client.call<EngineerTodayPayload>('/api/v1/engineer/today'),
    refetchInterval: 15_000,
  })
}

export function useEngineerStatus() {
  const client = useClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { jobId: string; from: string; to: string }) =>
      client.call<unknown>(`/api/v1/engineer/job/${args.jobId}/status`, {
        method: 'POST',
        body: { from: args.from, to: args.to },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['engineer', 'today'] })
    },
  })
}

export function useEngineerCheckin() {
  const client = useClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (jobId: string) =>
      client.call<unknown>(`/api/v1/engineer/job/${jobId}/checkin`, { method: 'POST', body: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['engineer', 'today'] })
    },
  })
}

export function useEngineerAccessFailed() {
  const client = useClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { jobId: string; category: string; note: string }) =>
      client.call<unknown>(`/api/v1/engineer/job/${args.jobId}/access-failed`, {
        method: 'POST',
        body: { category: args.category, note: args.note },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['engineer', 'today'] })
    },
  })
}
